import React, { useEffect, useState } from "react";
import api from "../../../../../utils/axiosInstance";
import ConfirmModal from "../../../../../components/Modals/ConfirmModal";
import { btn } from "../../../../../components/Button/buttonStyles";

const EditServicePage = ({ vendorId, onUpdateComplete }) => {
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [initialSelection, setInitialSelection] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const { data: res } = await api.get(
          `/vendor-services/${vendorId}/admin-services`,
          { withCredentials: true }
        );

        const servicesData = res?.data || [];
        console.log("Services with selection:", servicesData);
        
        setServices(servicesData);

        const selected = new Set(
          servicesData.filter((s) => s.is_selected).map((s) => s.id)
        );
        setSelectedServices(selected);
        setInitialSelection(selected);
        setError("");
      } catch (error) {
        console.error("Error fetching services:", error);
        setError("Failed to load services");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vendorId]);

  const handleCheckboxChange = (serviceId) => {
    setSelectedServices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const persistChanges = async (toAdd, toRemove) => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      for (const serviceId of toAdd) {
        await api.post(
          "/vendor-services",
          { vendor_id: vendorId, service_id: serviceId },
          { withCredentials: true }
        );
      }

      for (const serviceId of toRemove) {
        const vendorServicesRes = await api.get(`/vendor-services`, {
          withCredentials: true,
        });

        const vendorService = vendorServicesRes.data.data.find(
          (vs) => vs.vendor_id === parseInt(vendorId) && vs.service_id === serviceId
        );

        if (vendorService) {
          await api.delete(`/vendor-services/${vendorService.id}`, {
            withCredentials: true,
          });
        }
      }

      setSuccessMessage("Services updated successfully!");
      setTimeout(() => {
        if (typeof onUpdateComplete === "function") {
          onUpdateComplete();
        }
      }, 1500);
    } catch (error) {
      console.error("Error saving services:", error);
      setError("Failed to update services. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const currentSelection = Array.from(selectedServices);
    const previousSelection = Array.from(initialSelection);

    const toAdd = currentSelection.filter((id) => !previousSelection.includes(id));
    const toRemove = previousSelection.filter((id) => !currentSelection.includes(id));

    if (toRemove.length > 0) {
      const servicesToRemove = services.filter((s) => toRemove.includes(s.id));
      const totalPrices = servicesToRemove.reduce((sum, s) => sum + (s.price_count || 0), 0);

      if (totalPrices > 0) {
        setConfirm({
          title: "Remove Services?",
          message: `This will delete ${totalPrices} price${totalPrices === 1 ? "" : "s"} from ${toRemove.length} service${toRemove.length === 1 ? "" : "s"}. Continue?`,
          onConfirm: () => {
            setConfirm(null);
            persistChanges(toAdd, toRemove);
          },
        });
        return;
      }
    }

    persistChanges(toAdd, toRemove);
  };

  const handleSelectAll = () => {
    setSelectedServices(new Set(services.map((s) => s.id)));
  };

  const handleDeselectAll = () => {
    setSelectedServices(new Set());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <p className="text-gray-500 text-sm animate-pulse">Loading services...</p>
      </div>
    );
  }

  return (
    <div>
      {successMessage && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 rounded mb-3">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 text-sm px-3 py-2 rounded mb-3 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="text-danger-900 hover:text-danger-700 font-bold ml-2"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{selectedServices.size}</span> of{" "}
          <span className="font-semibold text-gray-900">{services.length}</span> selected
        </div>
        <div className="flex gap-2 text-sm">
          <button
            onClick={handleSelectAll}
            disabled={saving}
            className={btn("secondary", "sm")}
          >
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            disabled={saving}
            className={btn("secondary", "sm")}
          >
            Deselect All
          </button>
        </div>
      </div>

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                <th className="px-3 py-2 text-center font-semibold w-12">Select</th>
                <th className="px-3 py-2 text-center font-semibold">Service Name</th>
                <th className="px-3 py-2 text-center font-semibold w-24">Prices</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-10 text-center text-gray-400 text-xs">
                    No services available.
                  </td>
                </tr>
              ) : (
                services.map((service, index) => {
                  const isSelected = selectedServices.has(service.id);
                  const rowBg = isSelected
                    ? "bg-brand-50"
                    : index % 2 === 0
                    ? "bg-gray-50"
                    : "bg-white";
                  return (
                    <tr
                      key={service.id}
                      className={`${rowBg} hover:bg-brand-50 transition-colors cursor-pointer`}
                      onClick={() => !saving && handleCheckboxChange(service.id)}
                    >
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(service.id)}
                          disabled={saving}
                          className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-900">
                        {service.name}
                      </td>
                      <td className="px-3 py-2 text-xs text-center text-gray-700">
                        {service.price_count || 0}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex mt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className={btn("primary", "sm")}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
          confirmText="Continue"
        />
      )}
    </div>
  );
};

export default EditServicePage;
